import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { UnitLabel } from '@/shared/config/config'
import useI18n from '@/shared/hooks/useI18n'
import useOrganizationDocuments from '@/shared/hooks/useOrganizationDocuments'
import {
  Button,
  CheckboxGroup,
  GenericDialog,
  InputGroup,
  NumericInputGroup
} from '@/common'
import { PreconsumerScrapMaterial } from '@/api/PreconsumerScrapMaterialModel'
import { useUpdatePreconsumerScrapMaterial } from '@/shared/hooks/api/preconsumerScrapMaterials'
import { useCreatePreconsumerScrapMaterial } from '@/shared/hooks/api/preconsumerScrapMaterials'
import { OrganizationDocumentUpload } from '@/shared/components'
import { useReEvaluateActivityLevelValidationForMethodEmissionReport } from '@/hooks/api/emissionReports'
import ScrapTypeSelect from './ScrapTypeSelect'

type State = {
  details: string | null
  internalId: string
  quantity: string
  proofFilePath: string
  confirmation: boolean
}

const getInitialState = (initialState?: PreconsumerScrapMaterial) => ({
  internalId: initialState?.internalId ?? '',
  quantity: initialState?.quantityInTonnes?.toString() ?? '',
  proofFilePath: initialState?.proofFilePath ?? '',
  confirmation: initialState ? true : false,
  details: initialState?.details ?? null
})

export default function AddPreconsumerScrapDialog({
  isOpen,
  reportStartDate,
  close,
  emissionReportId,
  installationId,
  initialState,
  methodEmissionReportId
}: {
  isOpen: boolean
  reportStartDate: string
  close: () => void
  emissionReportId: string
  installationId: string
  initialState?: PreconsumerScrapMaterial
  methodEmissionReportId: string
}) {
  const { t } = useI18n()
  const { resetDocumentUploadState } = useOrganizationDocuments()
  const [isUploadingProof, setIsUploadingProof] = useState(false)
  const [newScrapType, setNewScrapType] = useState<string | null>(null)

  const {
    mutateAsync: createPreconsumerScrapMaterial,
    isPending: isCreatingPreconsumerScrapMaterial
  } = useCreatePreconsumerScrapMaterial(installationId)
  const {
    mutateAsync: updatePreconsumerScrapMaterial,
    isPending: isUpdatingPreconsumerScrapMaterial
  } = useUpdatePreconsumerScrapMaterial(emissionReportId)

  const {
    mutateAsync: reEvaluateActivityLevelValidationForMethodEmissionReport,
    isPending: isUpdatingActivityLevelValidation
  } = useReEvaluateActivityLevelValidationForMethodEmissionReport(
    emissionReportId,
    methodEmissionReportId
  )

  const [isEditMode, setIsEditMode] = useState(false)
  const [state, setState] = useState<State>(getInitialState())

  useEffect(() => {
    setIsEditMode(!!initialState)
    if (initialState) {
      setState(getInitialState(initialState))
      return
    }
    setState(getInitialState())
  }, [initialState])

  const { internalId, quantity, proofFilePath, confirmation, details } = state

  const resetState = useCallback(() => {
    setState(getInitialState(initialState))
    setNewScrapType(null)
    setIsEditMode(!!initialState)
    resetDocumentUploadState()
  }, [resetDocumentUploadState, initialState])

  const onAddClick = useCallback(async () => {
    try {
      await createPreconsumerScrapMaterial({
        stockChangeType: 'ALLOCATED',
        internalId,
        date: reportStartDate,
        quantityInTonnes: Number(quantity),
        proofFilePath,
        details: newScrapType ? newScrapType : details,
        emissionReportId
      })
      toast.success(t('report:materials.preconsumerScrap.dialog.toast.addedSuccess'))
    } catch (error) {
      console.error(error)
      toast.error(t('report:materials.preconsumerScrap.dialog.toast.addedError'))
    }

    reEvaluateActivityLevelValidationForMethodEmissionReport()

    resetState()
    close()
  }, [
    createPreconsumerScrapMaterial,
    close,
    resetState,
    newScrapType,
    internalId,
    quantity,
    details,
    t,
    proofFilePath,
    reportStartDate,
    emissionReportId,
    reEvaluateActivityLevelValidationForMethodEmissionReport
  ])

  const onEditClick = useCallback(async () => {
    if (!initialState) {
      return
    }
    try {
      await updatePreconsumerScrapMaterial({
        scrapMaterialId: initialState.id,
        payload: {
          proofFilePath,
          quantityInTonnes: Number(quantity),
          internalId,
          stockChangeType: initialState.stockChangeType,
          date: initialState.date,
          details: newScrapType ? newScrapType : details
        }
      })
      toast.success(t('report:materials.preconsumerScrap.dialog.toast.updateSuccess'))
    } catch (error) {
      console.error(error)
      toast.error(t('report:materials.preconsumerScrap.dialog.toast.updatedError'))
    }

    reEvaluateActivityLevelValidationForMethodEmissionReport()

    resetState()
    close()
  }, [
    initialState,
    resetState,
    close,
    newScrapType,
    updatePreconsumerScrapMaterial,
    proofFilePath,
    details,
    quantity,
    internalId,
    t,
    reEvaluateActivityLevelValidationForMethodEmissionReport
  ])

  const onScrapTypeChange = useCallback(
    (scrapType: string | null) => {
      setState({ ...state, details: scrapType ?? null })
    },
    [setState, state]
  )

  const onClose = useCallback(() => {
    resetState()
    close()
  }, [resetState, close])

  const isAddOrEditDisabled =
    (!details && !newScrapType) ||
    !quantity ||
    !confirmation ||
    isCreatingPreconsumerScrapMaterial ||
    isUpdatingPreconsumerScrapMaterial ||
    isUploadingProof ||
    isUpdatingActivityLevelValidation

  return (
    <GenericDialog
      isOpen={isOpen}
      size="xl"
      close={onClose}
      title={
        isEditMode
          ? t('report:materials.preconsumerScrap.dialog.editTitle')
          : t('report:materials.preconsumerScrap.dialog.addTitle')
      }
    >
      <div className="space-y-4 mb-6">
        <ScrapTypeSelect
          installationId={installationId}
          scrapType={details}
          newScrapType={newScrapType}
          onScrapTypeChange={onScrapTypeChange}
          onNewScrapTypeChange={setNewScrapType}
        />
        <div className="grid grid-cols-3 gap-4">
          <NumericInputGroup
            label={t('report:materials.preconsumerScrap.dialog.fields.quantity.label')}
            value={quantity}
            suffix={UnitLabel.t}
            onChange={evt => setState({ ...state, quantity: evt.target.value })}
            numericFormat={{
              decimalScale: 3,
              allowNegative: false
            }}
          />
          <div className="col-span-2">
            <InputGroup
              label={t('report:materials.preconsumerScrap.dialog.fields.internalId.label')}
              value={internalId}
              onChange={evt => setState({ ...state, internalId: evt.target.value })}
            />
          </div>
        </div>
        <OrganizationDocumentUpload
          label={t('report:materials.preconsumerScrap.dialog.fields.evidence.label')}
          fileName={proofFilePath}
          onDocumentUploaded={file => setState(st => ({ ...st, proofFilePath: file }))}
          onDocumentDeleted={() => setState(st => ({ ...st, proofFilePath: '' }))}
          disabled={false}
          onUploading={setIsUploadingProof}
        />
        <CheckboxGroup
          className="mr-2"
          label={t('report:materials.preconsumerScrap.dialog.fields.confirmation.label')}
          id="confirmation"
          checked={confirmation}
          onChange={checked => setState({ ...state, confirmation: checked })}
        />
      </div>
      <footer className="gap-2 flex justify-end">
        <Button
          variant="neutral"
          onClick={onClose}
          className="min-w-24"
          disabled={isUploadingProof}
        >
          {t('common:actions.cancel')}
        </Button>
        <Button
          disabled={isAddOrEditDisabled}
          onClick={isEditMode ? onEditClick : onAddClick}
          className="min-w-24"
        >
          {isEditMode ? t('common:actions.edit') : t('common:actions.add')}
        </Button>
      </footer>
    </GenericDialog>
  )
}
